import { useState } from "react";
import { FalhaDaApi, pedir } from "../api.js";
import { ExplicacaoDoItem } from "./ExplicacaoDoItem.js";
import { Icone } from "./Icone.js";
import { Marca } from "./Marca.js";
import { IconeIa } from "./icones.js";

/**
 * Perguntar sobre o item que está na tela — RN-62.
 *
 * Dois usos, um componente: **dúvida** durante a sessão ("me explica isto") e
 * **erro** na revisão ("por que eu errei"). O que muda é a rota e a frase do
 * botão; o resto — pedir, esperar, mostrar, tratar a falha — é o mesmo, e
 * duplicá-lo faria os dois divergirem no primeiro conserto.
 *
 * **A resposta é Markdown, renderizada pelo caminho que já existe.** Ela passa
 * por `ExplicacaoDoItem`, que converte e entrega ao `ConteudoRico` — o mesmo
 * saneador por lista de permissão de toda a aplicação (invariante 17). Este é o
 * único texto do app que não veio nem do usuário nem do importador, e é
 * justamente por isso que ele não ganha um caminho de renderização próprio.
 *
 * **Nada é gravado.** A resposta vive na tela até a pessoa sair. Guardá-la
 * sozinha encheria o caderno de texto que ninguém escolheu manter — e o que
 * vale a pena guardar, a pessoa copia para a página do assunto, que está logo
 * acima no painel.
 */
export function PerguntarAIa({
  itemId,
  modo,
}: {
  itemId: string;
  /** `duvida` pergunta sobre o item; `erro` pergunta sobre a marcação errada. */
  modo: "duvida" | "erro";
}) {
  const [pergunta, setPergunta] = useState("");
  const [resposta, setResposta] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function perguntar() {
    setOcupado(true);
    setErro(null);
    try {
      const r =
        modo === "duvida"
          ? await pedir("ia.tirarDuvida", { params: { id: itemId }, corpo: { pergunta } })
          : await pedir("ia.explicarErro", { params: { id: itemId } });
      setResposta(r.markdown);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível perguntar agora.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="pilha perguntar-ia">
      {modo === "duvida" && (
        <div className="campo">
          <label className="campo__rotulo" htmlFor={`ia-pergunta-${itemId}`}>
            Perguntar sobre esta questão
          </label>
          <textarea
            id={`ia-pergunta-${itemId}`}
            className="campo__entrada campo__area"
            rows={2}
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
            placeholder="Vazio pede a explicação da questão inteira."
          />
        </div>
      )}

      <button
        type="button"
        className={modo === "erro" ? "link-mao perguntar-ia__gatilho" : "botao botao--secundario botao--compacto"}
        disabled={ocupado}
        onClick={() => void perguntar()}
      >
        {/* A espera da IA é a marca girando (ADR 039), e não um ícone parado. */}
        {ocupado ? <Marca girando className="marca--pequena" /> : <Icone glifo={IconeIa} papel="acao" tamanho="p" />}
        {ocupado ? "perguntando…" : modo === "duvida" ? "Perguntar" : "por que errei?"}
      </button>

      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}

      {resposta && (
        <div className="postit perguntar-ia__resposta" role="status">
          <p className="postit__titulo">Resposta do modelo, confira antes de acreditar</p>
          <ExplicacaoDoItem markdown={resposta} />
        </div>
      )}
    </div>
  );
}
